import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as FramerMotion from 'framer-motion';
const { motion, AnimatePresence } = FramerMotion;
import { ArrowRight, Quote, Check, ChevronLeft, ChevronRight, Linkedin } from 'lucide-react';

import { Helmet } from 'react-helmet-async';
import { fetchCollection } from '../lib/cms';
import { colors, testimonialsData, servicesData } from '../data';
import { getLocalized } from '../lib/utils';

const Home = () => {
  const [services, setServices] = useState(servicesData);
  const [testimonials, setTestimonials] = useState(testimonialsData);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    fetchCollection('services').then(data => {
      if (Array.isArray(data) && data.length > 0) setServices(data);
    });
    fetchCollection('testimonials').then((data) => {
      if (!data || !Array.isArray(data) || data.length === 0) return;

      // Filter out potential duplicates based on name and quote snippet
      const unique = data.filter((item, index, self) => 
        index === self.findIndex((t) => {
          const name1 = String(getLocalized(t.name) || '');
          const name2 = String(getLocalized(item.name) || '');
          const quote1 = String(getLocalized(t.quote) || '').substring(0, 10);
          const quote2 = String(getLocalized(item.quote) || '').substring(0, 10);
          return name1 === name2 && quote1 === quote2;
        })
      );

      // Sort so Henning Kristiansen is first
      const sorted = [...unique].sort((a, b) => {
        const nameA = String(getLocalized(a.name) || '');
        const nameB = String(getLocalized(b.name) || '');
        if (nameA.toLowerCase().includes('henning kristiansen')) return -1;
        if (nameB.toLowerCase().includes('henning kristiansen')) return 1;
        return 0;
      });

      setTestimonials(sorted);
    });
  }, []);

  return (
    <div className="overflow-hidden">
      <Helmet>
        <title>Pia Meyer | Meyer Management ApS - Professionel Ledelsesrådgivning</title>
        <meta name="description" content="Jeg hjælper virksomheder med at skabe en stærk økonomifunktion, hvor trivsel og resultater går hånd i hånd." />
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-20 bg-white">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/assets/b8a7f302-f5bc-457e-a48c-e9d29dfda424/e25af159-a7db-46b4-8601-2525c839cc07.jpg" 
            alt="Pia Meyer - Meyer Management" 
            className="w-full min-[1200px]:w-1/2 h-full object-cover object-center min-[1200px]:object-right absolute right-0"
          />
          <div className="absolute top-0 right-0 h-full w-full min-[1200px]:w-1/2 bg-gradient-to-r from-white via-white/70 min-[1200px]:from-white min-[1200px]:via-transparent to-transparent pointer-events-none"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <h1 className={`text-[2.2rem] md:text-7xl font-serif font-bold ${colors.contrast} leading-tight mb-6`}>
              Få den økonomifunktion du <span className="italic font-normal">drømmer</span> om
            </h1>
            <p className="text-lg md:text-xl text-stone-600 mb-10 leading-relaxed">
              Jeg hjælper virksomheder med at skabe en stærk økonomifunktion, hvor trivsel og resultater går hånd i hånd
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/kontakt" 
                className={`px-8 py-4 rounded-full text-white ${colors.contrastBg} font-medium text-center hover:opacity-90 transition-all shadow-lg shadow-stone-200`}
              >
                Kontakt mig
              </Link>
              <Link 
                to="/ydelser" 
                className="px-8 py-4 rounded-full bg-white text-stone-800 font-medium text-center border border-stone-200 hover:bg-stone-50 transition-all"
              >
                Læs mere om mine ydelser
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="bg-white overflow-hidden">
        <div className="flex flex-col lg:flex-row items-stretch">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="hidden lg:block w-full lg:w-1/2 relative min-h-[400px] lg:min-h-[700px]"
          >
            <img 
              src="https://assets.auxodev.dk/assets/b8a7f302-f5bc-457e-a48c-e9d29dfda424/8917e708-7e6b-4306-8fac-a594c11aa3f1.jpg" 
              alt="Pia Meyer i samtale" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
          
          <div className="w-full lg:w-1/2 flex items-center">
            <div className="py-16 lg:py-24 px-6 sm:px-12 lg:pl-16 lg:pr-8 xl:pl-24 max-w-2xl">
              <h2 className={`text-3xl md:text-4xl font-serif font-bold ${colors.contrast} mb-6`}>
                Et samarbejde der skaber stærke økonomifunktioner
              </h2>
              <div className="space-y-4 text-stone-600 leading-relaxed mb-8">
                <p>
                  Jeg hjælper økonomifunktioner med at skabe den balance hvor mennesker, processer og teknologi arbejder sammen i stedet for at stå i vejen for hinanden.
                </p>
                <p>
                  Når strukturen er tydelig, rollerne er klare og relationerne stærke, leverer økonomifunktionen hurtigere, mere sikkert og med større overskud i hverdagen.
                </p>
                <p>
                  Mit arbejde handler om at skabe en økonomifunktion der fungerer både i praksis og i samarbejdet.
                </p>
                <p>
                  Jeg går tæt på teamet, processerne og de digitale værktøjer og hjælper jer med at få styr på de områder, der skaber mest støj og pres.
                </p>
                <p>
                  Resultatet er en mere stabil økonomifunktion, der kan levere kvalitet, tempo og trivsel på samme tid.
                </p>
                <p>
                  Jeg fungerer som en betroet partner der skaber ro, retning og fremdrift i både drift og udvikling.
                </p>
              </div>
              
              <div className="mb-8">
                <p className="font-bold mb-4">Med mig ved jeres side får i:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Check size={20} className="text-black shrink-0 mt-0.5" />
                    <span>Klar struktur og bedre samarbejde</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={20} className="text-black shrink-0 mt-0.5" />
                    <span>Hurtigere månedsluk med højere datakvalitet</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={20} className="text-black shrink-0 mt-0.5" />
                    <span>En leder der har støtte, sparring og overblik</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={20} className="text-black shrink-0 mt-0.5" />
                    <span>Et team der arbejder mere selvstændigt</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={20} className="text-black shrink-0 mt-0.5" />
                    <span>Større trivsel og mere overskud i teamet</span>
                  </li>
                </ul>
              </div>

              <Link to="/kontakt" className={`inline-flex items-center px-6 py-3 border border-[#204f3d] text-[#204f3d] rounded-full font-medium hover:bg-[#204f3d] hover:text-white transition-all`}>
                Ja tak, jeg vil gerne høre mere
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className={`py-24 ${colors.bg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-5xl mx-auto mb-16">
            <h2 className={`text-3xl md:text-4xl font-serif font-bold ${colors.contrast} mb-6`}>
              Sådan hjælper jeg økonomifunktioner
            </h2>
            <div className="text-stone-600 space-y-4">
              <p>
                Jeg hjælper økonomifunktioner med at skabe mere klarhed, stærkere processer og bedre trivsel.
                Når struktur, roller og samarbejde er tydelige, får teamet ro, retning og overskud i hverdagen.
              </p>
              <p>
                Jeg arbejder både med det organisatoriske, det ledelsesmæssige og det praktiske — så I får en økonomifunktion, der fungerer i virkeligheden og ikke kun på papiret.
                Mit fokus er at styrke samarbejdet, sikre bedre datakvalitet og skabe en hverdag, hvor resultater og trivsel går hånd i hånd.
              </p>
              <p className="font-bold">Her er de områder, hvor jeg skaber mærkbar værdi for jer:</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services
              .filter(service => {
                const path = getLocalized(service.path);
                return path !== 'forandringsprocesser' && path !== 'podcast' && path !== 'netvaerk' && path !== 'netværk';
              })
              .map((service, index) => {
                return (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white p-8 shadow-sm border border-stone-100 transition-all"
                  >
                    <h3 className={`text-xl font-serif font-bold ${colors.contrast} mb-4 transition-colors`}>
                      {getLocalized(service.title)}
                    </h3>
                    <p className="text-stone-600 text-sm mb-0 leading-relaxed">
                      {getLocalized(service.summary)}
                    </p>
                  </motion.div>
                );
              })}
          </div>
          
          <div className="mt-16 text-center">
            <Link 
              to="/ydelser" 
              className={`inline-block px-8 py-4 rounded-full border-2 border-stone-200 ${colors.contrast} font-medium hover:bg-stone-50 transition-all`}
            >
              Se alle ydelser
            </Link>
          </div>
        </div>
      </section>

      {/* Relationer Section */}
      <section className="bg-white overflow-hidden">
        <div className="flex flex-col lg:flex-row items-stretch">
          <div className="w-full lg:w-1/2 flex items-center lg:order-1 order-2">
            <div className="py-16 lg:py-24 px-6 sm:px-12 lg:pr-16 lg:pl-8 xl:pr-24 max-w-2xl ml-auto">
              <h2 className={`text-3xl md:text-4xl font-serif font-bold ${colors.contrast} mb-6`}>
                Nærværende ledelse starter med relationer
              </h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                Jeg tror på at god ledelse starter med ægte nærvær og gode relationer samt en vedvarende nysgerrighed på mennesker.
                Gennem mange års erfaring som økonomileder og økonomikonsulent har jeg oplevet, hvordan trivsel og tillid er fundamentet for bæredygtige resultater.
              </p>
              <Link to="/ydelser" className={`inline-flex items-center px-6 py-3 border border-[#204f3d] text-[#204f3d] rounded-full font-medium hover:bg-[#204f3d] hover:text-white transition-all`}>
                Se mine ydelser
              </Link>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="w-full lg:w-1/2 relative min-h-[400px] lg:min-h-[600px] lg:order-2 order-1"
          >
            <img 
              src="https://assets.auxodev.dk/assets/b8a7f302-f5bc-457e-a48c-e9d29dfda424/bb329dc6-4b27-460f-9830-9229efa17a44.jpg" 
              alt="Pia Meyer" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* How I Work Section */}
      <section className="bg-stone-50 overflow-hidden">
        <div className="flex flex-col lg:flex-row items-stretch">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="w-full lg:w-1/2 relative min-h-[400px] lg:min-h-[600px]"
          >
            <img 
              src="https://assets.auxodev.dk/assets/b8a7f302-f5bc-457e-a48c-e9d29dfda424/ca252a42-8dae-4eff-aea4-f88694b3962f.jpg" 
              alt="Sådan arbejder Pia Meyer" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
          
          <div className="w-full lg:w-1/2 flex items-center">
            <div className="py-16 lg:py-24 px-6 sm:px-12 lg:pl-16 lg:pr-8 xl:pl-24 max-w-2xl">
              <h2 className={`text-3xl md:text-4xl font-serif font-bold ${colors.contrast} mb-8`}>
                Sådan arbejder jeg
              </h2>
              <div className="text-stone-600 space-y-6 leading-relaxed mb-10">
                <p>
                  Jeg tror på, at en stærk økonomifunktion bygger på balance mellem mennesker, systemer og struktur.
                </p>
                <p>
                  Mit arbejde handler ikke kun om tal, men om at skabe teams, der trives, tager ansvar og skaber bæredygtige resultater sammen.
                </p>
                <p>
                  Jeg går tæt på både processer og mennesker. Det betyder, at jeg arbejder hands-on med optimering, men også med kultur og samarbejde.
                  For mig er det vigtigt, at vi ikke bare skaber struktur men en fælles forståelse for, hvorfor den er vigtig.
                </p>
                <p>
                  Samarbejdet starter altid med en samtale, hvor vi afklarer jeres behov og ønsker.
                  Derefter designer jeg et skræddersyet forløb, der giver overblik, kvalitet og fremdrift uden at gå på kompromis med trivsel og motivation.
                </p>
                <p>
                  Målet er altid det samme: at skabe en økonomifunktion, der fungerer som et samlet team, hvor mennesker og resultater udvikles side om side.
                </p>
              </div>
              <Link to="/ydelser" className={`inline-flex items-center px-6 py-3 border border-[#204f3d] text-[#204f3d] rounded-full font-medium hover:bg-[#204f3d] hover:text-white transition-all`}>
                Læs mere om mine ydelser og metoder
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* LinkedIn Newsletter Section */}
      <section className="bg-white overflow-hidden border-t border-stone-100">
        <div className="flex flex-col lg:flex-row items-stretch">
          <div className="w-full lg:w-1/2 flex items-center lg:order-1 order-2">
            <div className="py-16 lg:py-24 px-6 sm:px-12 lg:pr-16 lg:pl-8 xl:pr-24 max-w-2xl ml-auto">
              <div className="flex flex-wrap gap-2 mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                  <Linkedin size={16} />
                  <span>Nyhedsbrev på LinkedIn</span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-stone-100 text-stone-700 rounded-full text-sm font-medium">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  <span>320+ abonnenter</span>
                </div>
              </div>
              <h2 className={`text-3xl md:text-4xl font-serif font-bold ${colors.contrast} mb-6`}>
                Menneskelig ledelse
              </h2>
              <div className="text-stone-600 space-y-4 leading-relaxed mb-8">
                <p>
                  Vil du have konkrete råd, personlige refleksioner og faglige indspark om, hvordan vi skaber stærke økonomifunktioner, hvor både tal og trivsel går op i en højere enhed?
                </p>
                <p>
                  I mit nyhedsbrev <strong>'Menneskelig ledelse'</strong> på LinkedIn deler jeg ud af mine erfaringer som økonomileder og rådgiver. Nyhedsbrevet har i dag over 320 abonnenter, som læser med, når jeg skriver om nærværende ledelse, optimering af arbejdsprocesser, og hvordan vi opbygger teams, der trives og leverer resultater med overskud.
                </p>
                <p>
                  Tilmeld dig og bliv en del af et fællesskab, der sætter det hele menneske i centrum for økonomisk succes og stærkt samarbejde.
                </p>
              </div>
              <a 
                href="https://www.linkedin.com/newsletters/menneskelig-ledelse-7414936384207282176/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#0077B5] text-white rounded-full font-medium hover:bg-[#006297] transition-all shadow-lg shadow-blue-100"
              >
                <Linkedin size={20} />
                <span>Læs og abonner på LinkedIn</span>
              </a>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="w-full lg:w-1/2 relative min-h-[400px] lg:min-h-[600px] lg:order-2 order-1"
          >
            <img 
              src="https://assets.auxodev.dk/assets/b8a7f302-f5bc-457e-a48c-e9d29dfda424/32fb45e8-6da2-4186-9f63-1734dcb6da21.jpg" 
              alt="Pia Meyer - Menneskelig ledelse nyhedsbrev" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-24 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-16 text-center">
              <h2 className={`text-3xl md:text-4xl font-serif font-bold ${colors.contrast} mb-4`}>
                Det siger mine kunder
              </h2>
              <p className="text-stone-600 max-w-2xl mx-auto">
                Her er et udpluk af de tilbagemeldinger, jeg har fået fra ledere og samarbejdspartnere.
              </p>
            </div>
            
            <div className="relative">
              <div className="relative min-h-[400px] flex items-center justify-center">
                <AnimatePresence mode="wait">
                  <motion.div 
                    key={testimonials[currentTestimonial].id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className="w-full bg-white p-8 md:p-12 border border-stone-100 shadow-sm flex flex-col items-center text-center"
                  >
                    <Quote className="text-[#204f3d]/20 mb-8" size={48} />
                    <p className="text-stone-600 italic mb-10 text-lg md:text-xl leading-relaxed whitespace-pre-line max-w-5xl">
                      "{String(getLocalized(testimonials[currentTestimonial].quote) || '').replace(/\\n/g, '\n')}"
                    </p>
                    <div className="pt-8 border-t border-stone-100 w-full max-w-xs">
                      <p className={`font-bold text-lg ${colors.contrast}`}>
                        {getLocalized(testimonials[currentTestimonial].name)}
                      </p>
                      <p className="text-stone-400 text-sm">
                        {getLocalized(testimonials[currentTestimonial].role)}
                      </p>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {testimonials.length > 1 && (
                <>
                  <div className="flex justify-center mt-12 gap-4">
                    <button 
                      onClick={prevTestimonial}
                      className="p-3 rounded-full border border-stone-200 text-stone-400 hover:text-stone-800 hover:border-stone-800 transition-all"
                      aria-label="Forrige udtalelse"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button 
                      onClick={nextTestimonial}
                      className="p-3 rounded-full border border-stone-200 text-stone-400 hover:text-stone-800 hover:border-stone-800 transition-all"
                      aria-label="Næste udtalelse"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </div>
                  
                  <div className="flex justify-center mt-6 gap-2">
                    {testimonials.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentTestimonial(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          idx === currentTestimonial ? 'bg-[#204f3d] w-6' : 'bg-stone-200'
                        }`}
                        aria-label={`Gå til udtalelse ${idx + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`${colors.contrastBg} p-5 md:p-20 text-center text-white relative overflow-hidden shadow-2xl`}>
            <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -ml-32 -mt-32 blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mb-32 blur-3xl"></div>
            
            <h2 className="text-3xl md:text-5xl font-serif font-bold mb-8 relative z-10">
              Skal vi tage en kort samtale?
            </h2>
            <p className="text-lg text-stone-300 mb-12 max-w-2xl mx-auto relative z-10">
              Jeg tror på, at god ledelse og stærke økonomifunktioner starter med en ærlig samtale. Lad os tale om, hvordan jeg kan støtte jer i at skabe nærvær, struktur og trivsel
            </p>
            <Link 
              to="/kontakt" 
              className="inline-block px-10 py-5 bg-white text-stone-900 rounded-full font-bold text-lg hover:bg-stone-100 transition-all shadow-xl relative z-10"
            >
              Ja tak – lad os det
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
